<script>
  import { getApiUrl } from '../api';
  import { authStore } from '../authStore';
  import { selectedCountry } from '../countryStore';
  import { t } from '../i18nStore';
  import { getCurrencySymbol } from '../utils';
  import PromotionCard from '../components/PromotionCard.svelte';
  
  let fileInput;
  let isUploading = false;
  let isSearching = false;
  let error = '';
  
  let billData = null;
  let recommendedPromos = [];
  
  let currentUser = null;
  authStore.subscribe(value => { currentUser = value.user; });

  $: currency = getCurrencySymbol($selectedCountry);

  function triggerFileInput() {
      fileInput.click();
  }

  async function handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      if (file.type !== 'application/pdf') {
          error = "Please upload a PDF file.";
          return;
      }

      isUploading = true;
      error = '';
      billData = null;
      recommendedPromos = [];

      const formData = new FormData();
      formData.append('file', file);
      formData.append('countryCode', $selectedCountry);
      if (currentUser && currentUser.idTokenClaims) {
          formData.append('userId', currentUser.idTokenClaims.oid);
      }

      try {
          const res = await fetch(getApiUrl('/bills/upload'), {
              method: 'POST',
              body: formData
          });
          
          const result = await res.json();
          if (!res.ok) throw new Error(result.error || "Failed to upload bill.");
          
          billData = result;
          
          // Trigger search automatically after successful extraction
          if (billData.billAmount > 0) {
              await searchOffers(billData.billAmount);
          } else {
              error = "Could not extract a valid bill amount. Please ensure the PDF is clear.";
          }

      } catch (e) {
          error = e.message;
      } finally {
          isUploading = false;
          // Reset input
          event.target.value = '';
      }
  }

  async function searchOffers(amount) {
      isSearching = true;
      try {
          // 1. Search using the general search endpoint targeting "Bill Payment" category and the spending amount
          const params = new URLSearchParams({
              categories: 'Bill Payment',
              spending: amount,
              countryCode: $selectedCountry,
              page: 1,
              pageSize: 50 // Fetch enough to sort locally
          });
          
          const res = await fetch(getApiUrl(`/promotions/search?${params.toString()}`));
          const result = await res.json();
          
          if (!res.ok) throw new Error("Failed to fetch offers.");
          
          let promos = result.data || [];
          
          // 2. Sort locally by Maximum Reward Percentage (Highest First)
          // The backend might already sort, but this guarantees the "biggest reward first" logic
          promos.sort((a, b) => {
              const rewardA = a.maximumRewardPercentage || 0;
              const rewardB = b.maximumRewardPercentage || 0;
              return rewardB - rewardA; // Descending
          });
          
          recommendedPromos = promos;
          
      } catch (e) {
          error = e.message;
      } finally {
          isSearching = false;
      }
  }
</script>

<div class="analyzer-container">
  
  <div class="header">
      <h2>{$t('bill.title')}</h2>
      <p>{$t('bill.subtitle')}</p>
  </div>

  <!-- Upload Area -->
  <div class="upload-area {isUploading ? 'disabled' : ''}" on:click={!isUploading ? triggerFileInput : null}>
      <input 
          type="file" 
          accept="application/pdf" 
          bind:this={fileInput} 
          on:change={handleFileUpload} 
          style="display: none;" 
      />
      <div class="upload-content">
          <span class="upload-icon">📄</span>
          <p>{$t('bill.upload_prompt')}</p>
          {#if isUploading}
              <div class="spinner"></div>
              <p class="status-text">{$t('bill.analyzing')}</p>
          {/if}
      </div>
  </div>

  {#if error}
      <div class="error-banner">{error}</div>
  {/if}

  <!-- Extracted Bill Info -->
  {#if billData && !isUploading}
      <div class="bill-details-card">
          <h3>{$t('bill.details')}</h3>
          <div class="details-grid">
              <div class="detail-item">
                  <span class="label">{$t('bill.company')}:</span>
                  <span class="value">{billData.companyName || 'Unknown'}</span>
              </div>
              <div class="detail-item">
                  <span class="label">{$t('bill.amount')}:</span>
                  <span class="value amount">{currency}{(billData.billAmount || 0).toFixed(2)}</span>
              </div>
              <div class="detail-item">
                  <span class="label">{$t('bill.due_date')}:</span>
                  <span class="value">{billData.paymentDueDate || 'N/A'}</span>
              </div>
          </div>
      </div>

      <!-- Recommended Offers -->
      <div class="offers-section">
          <h3>{$t('bill.best_offers')}</h3>
          
          {#if isSearching}
              <p class="status-text">{$t('bill.finding_offers')}</p>
          {:else if recommendedPromos.length === 0}
              <p class="no-offers">{$t('bill.no_offers')}</p>
          {:else}
              <div class="promo-list">
                  {#each recommendedPromos as promo (promo._id)}
                      <!-- PromotionCard already handles the "Join Challenge" logic internally -->
                      <PromotionCard {promo} />
                  {/each}
              </div>
          {/if}
      </div>
  {/if}

</div>

<style>
  .analyzer-container {
      max-width: 900px;
      margin: 2rem auto;
      padding: 0 1rem;
  }
  
  .header { text-align: center; margin-bottom: 2rem; }
  .header h2 { color: #333; margin-bottom: 0.5rem; }
  .header p { color: #666; font-size: 1.1rem; }

  .upload-area {
      border: 2px dashed #007bff;
      border-radius: 12px;
      padding: 3rem 2rem;
      text-align: center;
      background: #f8fbff;
      cursor: pointer;
      transition: all 0.3s ease;
      margin-bottom: 2rem;
  }
  .upload-area:hover:not(.disabled) {
      background: #e6f0ff;
      border-color: #0056b3;
  }
  .upload-area.disabled {
      cursor: not-allowed;
      opacity: 0.7;
  }
  .upload-icon { font-size: 3rem; margin-bottom: 1rem; display: block; }
  .upload-content p { color: #495057; font-weight: 500; font-size: 1.1rem; }
  
  .spinner {
      border: 4px solid rgba(0,0,0,0.1); width: 36px; height: 36px; border-radius: 50%;
      border-left-color: #007bff; animation: spin 1s linear infinite; margin: 1rem auto;
  }
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
  
  .status-text { color: #007bff; font-weight: bold; margin-top: 0.5rem; }

  .error-banner {
      background: #f8d7da; color: #721c24; padding: 1rem; border-radius: 6px;
      border: 1px solid #f5c6cb; margin-bottom: 2rem; text-align: center;
  }

  .bill-details-card {
      background: #fff; padding: 1.5rem; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.05);
      margin-bottom: 2.5rem; border-left: 5px solid #28a745;
  }
  .bill-details-card h3 { margin-top: 0; color: #333; margin-bottom: 1rem; }
  
  .details-grid {
      display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem;
  }
  .detail-item { display: flex; flex-direction: column; gap: 0.3rem; }
  .detail-item .label { color: #6c757d; font-size: 0.9rem; text-transform: uppercase; letter-spacing: 0.5px; }
  .detail-item .value { font-size: 1.2rem; font-weight: 500; color: #333; }
  .detail-item .amount { color: #28a745; font-weight: bold; font-size: 1.4rem; }

  .offers-section h3 { color: #333; margin-bottom: 1.5rem; text-align: center; }
  .no-offers { text-align: center; color: #6c757d; font-style: italic; font-size: 1.1rem; }
  
  .promo-list {
      display: grid; grid-template-columns: 1fr; gap: 1.5rem;
  }
  @media (min-width: 600px) {
      .promo-list { grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); }
  }
</style>